import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ArrowLeft, ArrowRight, MoveHorizontal } from 'lucide-react';

const defaultItems = [
  {
    id: 1,
    title: 'FLEX PRINTING',
    description: 'Outdoor banners, hoardings, backdrops, and retail displays with sharp color and durable media.',
    imageUrl: 'https://images.unsplash.com/photo-1773525912489-9e04df48f639?q=80&w=900&auto=format&fit=crop',
    priceRange: 'Rs.10-Rs.70/sq ft',
    features: ['1440 DPI resolution', 'Weather-ready media', 'Large sizes', 'Same-day options'],
  },
  {
    id: 2,
    title: 'VINYL GRAPHICS',
    description: 'Vehicle wraps, wall graphics, window decals, and reflective signage with clean installation.',
    imageUrl: 'https://images.unsplash.com/photo-1759295199382-aa4f1bc50db2?q=80&w=900&auto=format&fit=crop',
    priceRange: 'Rs.25-Rs.85/sq ft',
    features: ['Eco-solvent printing', 'Lamination options', 'Cut-to-shape', '5+ year durability'],
  },
  {
    id: 3,
    title: 'FRAME WELDING',
    description: 'MS steel and aluminum support frames built for shop boards, LED signs, and rooftop structures.',
    imageUrl: 'https://images.unsplash.com/photo-1748348812466-8e29e1348f73?q=80&w=900&auto=format&fit=crop',
    priceRange: 'Rs.200-Rs.1000/kg',
    features: ['In-house fabrication', 'Powder coating', 'Rust treatment', 'Custom dimensions'],
  },
  {
    id: 4,
    title: 'INSTALLATION',
    description: 'Site survey, mounting, electrical coordination, and post-install support for signage projects.',
    imageUrl: 'https://images.unsplash.com/photo-1763596304839-bac0d4aadb25?q=80&w=900&auto=format&fit=crop',
    priceRange: 'Rs.500-Rs.5000',
    features: ['Safety protocols', 'Expert team', 'Site planning', 'Final quality check'],
  },
  {
    id: 5,
    title: 'EVENT DISPLAYS',
    description: 'Roll-up standees, promotional counters, event banners, and quick-turnaround campaign assets.',
    imageUrl: 'https://images.unsplash.com/photo-1761195689615-9469b65dac01?q=80&w=900&auto=format&fit=crop',
    priceRange: 'Rs.1500/piece',
    features: ['Portable setups', 'Reusable frames', 'Bulk support', 'Fast dispatch'],
  },
];

const getCardOffset = (index, activeIndex, total) => {
  const rawOffset = index - activeIndex;
  const half = total / 2;

  if (rawOffset > half) {
    return rawOffset - total;
  }

  if (rawOffset < -half) {
    return rawOffset + total;
  }

  return rawOffset;
};

function ServiceStackCard({ item, index, offset, isActive, onSelect }) {
  const clampedOffset = Math.max(-2, Math.min(2, offset));
  const depth = Math.abs(clampedOffset);
  const x = clampedOffset * 88;
  const y = depth * 26;
  const scale = 1 - depth * 0.07;
  const rotate = clampedOffset * -7;
  const zIndex = 10 - depth;
  const opacity = depth > 2 ? 0 : 1 - depth * 0.16;

  return (
    <button
      type="button"
      className={`service-stack-card ${isActive ? 'is-active' : ''}`}
      onClick={onSelect}
      aria-label={`Show ${item.title} service`}
      aria-pressed={isActive}
      style={{
        '--service-x': `${x}px`,
        '--service-y': `${y}px`,
        '--service-scale': scale,
        '--service-rotate': `${rotate}deg`,
        '--service-opacity': opacity,
        '--service-z': zIndex,
      }}
      data-testid={`service-stack-card-${index}`}
    >
      <div className="service-stack-card-inner">
        <img
          src={item.imageUrl}
          alt={item.title}
          className="service-stack-image"
          loading="lazy"
          onError={(event) => {
            event.currentTarget.onerror = null;
            event.currentTarget.src = `https://placehold.co/1100x760/0A0A0A/FF2A00?text=${encodeURIComponent(item.title)}`;
          }}
        />
        <div className="service-stack-shade" />

        <div className="service-stack-index">
          {String(index + 1).padStart(2, '0')}
        </div>

        <div className="service-stack-panel">
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="service-stack-price">{item.priceRange}</p>
              <h3>{item.title}</h3>
            </div>
            <div className="service-stack-icon" aria-hidden="true">
              {item.icon}
            </div>
          </div>
          <p className="service-stack-description">{item.description}</p>
          <div className="service-stack-features">
            {item.features.map((feature) => (
              <span key={feature}>
                <i aria-hidden="true" />
                {feature}
              </span>
            ))}
          </div>
        </div>

        <div className="service-stack-rail-title">
          {item.title}
        </div>
      </div>
    </button>
  );
}

export function ServicesAccordion({ items = defaultItems }) {
  const normalizedItems = useMemo(
    () =>
      items.map((item, index) => ({
        id: item.id ?? index + 1,
        ...item,
      })),
    [items]
  );
  const [activeIndex, setActiveIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const touchStartX = useRef(null);

  const nextService = () => {
    setActiveIndex((current) => (current + 1) % normalizedItems.length);
  };

  const prevService = () => {
    setActiveIndex((current) => (current - 1 + normalizedItems.length) % normalizedItems.length);
  };

  useEffect(() => {
    if (isPaused) {
      return undefined;
    }

    const interval = setInterval(nextService, 4600);
    return () => clearInterval(interval);
  }, [isPaused, normalizedItems.length]);

  const handleTouchStart = (event) => {
    touchStartX.current = event.touches[0].clientX;
    setIsPaused(true);
  };

  const handleTouchEnd = (event) => {
    if (touchStartX.current === null) {
      return;
    }

    const deltaX = event.changedTouches[0].clientX - touchStartX.current;
    touchStartX.current = null;

    if (Math.abs(deltaX) < 40) {
      return;
    }

    if (deltaX < 0) {
      nextService();
    } else {
      prevService();
    }
  };

  return (
    <div
      className="service-stack-shell"
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
      data-testid="services-accordion"
    >
      <div className="service-stack-stage">
        {normalizedItems.map((item, index) => (
          <ServiceStackCard
            key={item.id}
            item={item}
            index={index}
            offset={getCardOffset(index, activeIndex, normalizedItems.length)}
            isActive={index === activeIndex}
            onSelect={() => {
              setIsPaused(true);
              setActiveIndex(index);
            }}
          />
        ))}
      </div>

      <div className="service-stack-swipe-cue" aria-hidden="true">
        <MoveHorizontal className="h-4 w-4" strokeWidth={2.4} />
        <span>Swipe</span>
      </div>

      <div className="service-stack-controls">
        <button
          type="button"
          onClick={() => {
            setIsPaused(true);
            prevService();
          }}
          aria-label="Previous service"
          data-testid="service-prev"
        >
          <ArrowLeft className="h-5 w-5" strokeWidth={2} />
        </button>
        <div>
          {String(activeIndex + 1).padStart(2, '0')} / {String(normalizedItems.length).padStart(2, '0')}
        </div>
        <button
          type="button"
          onClick={() => {
            setIsPaused(true);
            nextService();
          }}
          aria-label="Next service"
          data-testid="service-next"
        >
          <ArrowRight className="h-5 w-5" strokeWidth={2} />
        </button>
      </div>

      <div className="service-stack-dots" aria-hidden="true">
        {normalizedItems.map((item, index) => (
          <span key={item.id} className={index === activeIndex ? 'is-active' : ''} />
        ))}
      </div>
    </div>
  );
}

export default ServicesAccordion;
